/* eslint-disable max-len */
import {
  useState, useCallback, useEffect, Dispatch, useMemo,
} from 'react';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';
import useToastNotification from 'hooks/useToastNotification';
import StaffDomain from 'domain/staff';
import { postSalaryInfo, deleteStaffSalary } from 'api/staff';
import SelfOrganizationDomain from 'domain/master/general/selfOrganization';
import { SalarySupportStaff, SalaryBusinessStaff, SalaryDataStaffDomain } from 'domain/master/general/salaryDataStaff';
import { getErrorMessageForResponse } from 'utility/errorUtil';
import { InfoMessage } from 'utility/messages';
import { LaborCostCustomItemMst, getList as getCustomItemMstList } from 'api/laborCostCustomItemMst';

export const useSalaryForm = (
  initialStaffDomain: StaffDomain = StaffDomain.generateInitial(),
  isEditMode: boolean = false,
  setIsLoading: Dispatch<React.SetStateAction<boolean>>,
  menuType: string,
) => {
  const history = useHistory();
  const [openManagerAddModal, setOpenManagerAddModal] = useState<boolean>(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [detailMode, setDetailMode] = useState(false);
  const { successNotification, errorNotification } = useToastNotification();
  const [deleteTargetStaffSalary, setDeleteTargetStaffSalary] = useState<SalaryDataStaffDomain|null>(null);

  const onSubmit = async (values: StaffDomain) => {
    if (!confirmModalOpen) {
      // 人件費カスタム項目の整数チェック
      const ciValues = (values.selectSalary as any).customItemValues;
      if (ciValues) {
        for (const key of Object.keys(ciValues)) {
          const val = ciValues[key];
          if (val !== undefined && val !== null && val !== '' && !Number.isInteger(Number(val))) {
            errorNotification('カスタム項目の金額は整数で入力してください。');
            return;
          }
        }
      }
      setConfirmModalOpen(true);
      return;
    }

    setConfirmModalOpen(false);
    setIsLoading(true);

    try {
      const response = await postSalaryInfo(isEditMode ? 1 : 0, menuType, values.getStaffInfoPostData(isEditMode));
      const updatedStaff = new StaffDomain(response.staff);
      formik.setFieldValue('salaryList', updatedStaff.salaryList);

      if (!isEditMode) {
        sessionStorage.setItem('selectedOrgCodeStaffMaster', updatedStaff.orgCode);
      }
      setIsLoading(false);
      successNotification(isEditMode ? '更新しました。' : '登録しました。');
      if (!isEditMode && response) {
        history.goBack();
      }
    } catch (error) {
      setIsLoading(false);
      errorNotification(getErrorMessageForResponse(error));
    }
  };
  const formik = useFormik({
    initialValues: initialStaffDomain,
    onSubmit,
  });

  useEffect(() => {
    formik.setValues(initialStaffDomain);
    formik.setFieldValue('selectSalary', initialStaffDomain.selectSalary);
    // customItemList → customItemValues に変換（画面入力用）
    const ciList = (initialStaffDomain.selectSalary as any).customItemList;
    if (ciList && Array.isArray(ciList)) {
      const values: { [key: string]: number } = {};
      ciList.forEach((item: any) => {
        const cid = item.id?.customId || item.customId;
        if (cid) values[cid] = item.amount || 0;
      });
      formik.setFieldValue('selectSalary.customItemValues', values);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialStaffDomain]);

  const closeConfirmModal = useCallback(() => {
    setConfirmModalOpen(false);
  }, []);

  const isOpenDeleteModal = useMemo(() => !!deleteTargetStaffSalary, [deleteTargetStaffSalary]);
  const closeDeleteModal = useCallback(() => setDeleteTargetStaffSalary(null), []);

  const onSubmitDeleteStaffSalary = useCallback(async () => {
    if (!deleteTargetStaffSalary) return;
    setIsLoading(true);
    try {
      const salaryList = await deleteStaffSalary(deleteTargetStaffSalary.staffCode, deleteTargetStaffSalary.staffSalaryId);
      initialStaffDomain.salaryList = salaryList;
      formik.setFieldValue('salaryList', salaryList);
      successNotification(InfoMessage.Common.DELETE_COMPLETED);
    } catch(error)  {
        errorNotification(getErrorMessageForResponse(error));
    } finally {
      setDeleteTargetStaffSalary(null);
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik, initialStaffDomain, successNotification, errorNotification]);

  const addOrganizationSupport = useCallback((orgList: Array<SelfOrganizationDomain>) => {
    if (!formik.values.selectSalary.salarySupportDataList) {
      return;
    }
    const supportDataList : Array<SalarySupportStaff> = orgList.map(
      (org) => {
        const support = {
          staffSalaryId: '',
          orgCode: org.orgCode,
          orgName: org.orgName,
          addHourlySalary: 0,
          supportTransportation: 0,
        };
        return support;
      },
    );

    formik.setFieldValue('selectSalary.salarySupportDataList', formik.values.selectSalary.salarySupportDataList.concat(supportDataList));
  }, [formik]);

  const deleteOrganizationSupport = useCallback((index: number) => () => {
    if (!formik.values.selectSalary.salarySupportDataList) {
      return;
    }
    formik.setFieldValue('selectSalary.salarySupportDataList', formik.values.selectSalary.salarySupportDataList.filter((manager, idx) => idx !== index));
  }, [formik]);

  const setOrganizationSupport = useCallback(
    (index: number) => (newManager: SalarySupportStaff) => {
      if (!formik.values.selectSalary.salarySupportDataList) {
        return;
      }
      formik.setFieldValue('selectSalary.salarySupportDataList', formik.values.selectSalary.salarySupportDataList.map((manager, idx) => (idx === index ? newManager : manager)));
    }, [formik],
  );


  // Bussiness form
  const addSalaryBusiness = useCallback(() => {
    if (!formik.values.selectSalary.salaryBusinessList) {
      return;
    }
    const salaryBusinessData : SalaryBusinessStaff = {
      id: {
        staffSalaryId: '',
        businessId: '',
      },
      addHourlySalary: 0,
    };
    formik.setFieldValue('selectSalary.salaryBusinessList', formik.values.selectSalary.salaryBusinessList.concat(salaryBusinessData));
  }, [formik]);

  const deleteSalaryBusiness = useCallback((index: number) => () => {
    if (!formik.values.selectSalary.salaryBusinessList) {
      return;
    }
    formik.setFieldValue('selectSalary.salaryBusinessList', formik.values.selectSalary.salaryBusinessList.filter((manager, idx) => idx !== index));
  }, [formik]);


  const setSalaryBusiness = useCallback(
    (index: number) => (newManager: SalaryBusinessStaff) => {
      if (!formik.values.selectSalary.salaryBusinessList) {
        return;
      }
      formik.setFieldValue('selectSalary.salaryBusinessList', formik.values.selectSalary.salaryBusinessList.map((manager, idx) => (idx === index ? newManager : manager)));
    }, [formik],
  );


  // 人件費カスタム項目マスタ取得
  const [customItemMstList, setCustomItemMstList] = useState<Array<LaborCostCustomItemMst>>([]);
  useEffect(() => {
    let isSubscribed = true;
    const companyCode = sessionStorage.getItem('loginUser.companyCode') || '';
    getCustomItemMstList(companyCode).then((response) => {
      if (isSubscribed) {
        setCustomItemMstList(response);
      }
    });
    return () => { isSubscribed = false; };
  }, []);

  return {
    formik,
    detailMode,
    setDetailMode,
    confirmModalOpen,
    closeConfirmModal,
    openManagerAddModal,
    setOpenManagerAddModal,
    addOrganizationSupport,
    deleteOrganizationSupport,
    setOrganizationSupport,
    addSalaryBusiness,
    deleteSalaryBusiness,
    setSalaryBusiness,
    onSubmitDeleteStaffSalary,
    setDeleteTargetStaffSalary,
    isOpenDeleteModal,
    closeDeleteModal,
    customItemMstList,
  };
};

export default {
  useSalaryForm,
};
